<template>
  <Popup
    class="inline-block"
    menuPaddingClass="py-2"
    :open="active"
    :menuWidth="menuWidth"
    :menuHeight="menuHeight"
    :useContainerWidth="useContainerWidth"
    :showLabel="showMenuTopLabel"
    :label="topLabel"
    @change="active = $event"
  >
    <template slot="anchor">
      <input
        v-show="active && showSearch"
        ref="search"
        type="search"
        class="form-input w-full"
        :class="[size ? `form-input-${size}` : '']"
        placeholder="Search"
        :value="filter"
        @input="e => (filter = e.target.value)"
      />
      <button
        v-show="!(active && showSearch)"
        id="options-menu"
        type="button"
        class="btn text-left w-full justify-start cursor-pointer overflow-hidden"
        :class="[
          buttonClass,
          theme,
          size ? `btn-${size}` : '',
          {
            'border-enterprise-primary': changed,
            'opacity-25 cursor-not-allowed': disabled,
            'h-full': fullHeight,
          },
          buttonGroupingStyle,
        ]"
        aria-haspopup="true"
        :aria-expanded="active"
        :disabled="disabled"
        @click="toggleMenu"
      >
        <template v-if="selectedOption.handle">
          <CircleIcon
            v-if="!hideReset && changed"
            class="mr-1.5 -ml-1 h-5 w-5 hover:text-red-400"
            @click.stop="selectOption(defaultOption)"
          />
          <span
            :style="selectedOption.styles"
            class="flex-1 whitespace-no-wrap overflow-hidden"
          >
            {{ selectedOption.label }}
          </span>
        </template>
        <span v-else-if="label" class="flex-1 overflow-hidden">{{
          label
        }}</span>
        <component
          :is="active ? 'ChevronUp' : 'ChevronDown'"
          class="h-4 w-4"
          :class="label || (changed && !hideReset) ? '-mr-1 ml-1.5' : 'mt-0'"
        />
      </button>
    </template>
    <div slot="content">
      <p v-if="showMenuLabel" class="text-sm px-4 mb-2">{{ label }}</p>
      <div
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="options-menu"
      >
        <button
          v-for="option in filteredOptions"
          :key="option.handle"
          class="cursor-pointer w-full text-left block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
          :class="{
            'font-bold text-enterprise-primary': option.handle === selected,
          }"
          :style="option.styles"
          role="menuitem"
          @click="selectOption(option)"
          v-html="option.label"
        ></button>
      </div>
    </div>
  </Popup>
</template>

<script>
import Popup from '@/components/Popup'
import ChevronDown from '@/assets/svg/icons/chevron-down.svg?inline'
import ChevronUp from '@/assets/svg/icons/chevron-up.svg?inline'
import CircleIcon from '@/assets/svg/icons/x-circle.svg?inline'

export default {
  components: { Popup, ChevronDown, CircleIcon, ChevronUp },

  props: {
    selected: { type: [String, Number], default: '' },
    buttonGrouping: { type: String, default: null },
    autoDefault: { type: Boolean, default: true },
    theme: { type: String, default: 'btn-naked' },
    hideReset: { type: Boolean, default: false },
    size: { type: String, default: 'small' },
    buttonClass: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    label: { type: String, default: '' },
    showMenuLabel: { type: Boolean, default: false },
    showSearch: { type: Boolean, default: false },
    menuHeight: { type: String, default: '15rem' },
    menuWidth: { type: String, default: '12.5rem' },
    useContainerWidth: { type: Boolean, default: false },
    disabled: { type: Boolean },
    showMenuTopLabel: { type: Boolean, default: false },
    topLabel: { type: String, default: null },
    fullHeight: { type: Boolean, default: true },
  },

  data: () => ({
    defaultOption: {},
    active: false,
    offset: 0,
    filter: '',
  }),

  computed: {
    selectedOption() {
      return this.find(this.selected) || {}
    },

    changed() {
      return this.selectedOption.handle !== this.defaultOption.handle
    },

    buttonGroupingStyle() {
      switch (this.buttonGrouping) {
        case 'left':
          return 'rounded-r-none'

        case 'middle':
          return 'rounded-none'

        case 'right':
          return 'rounded-l-none'

        default:
          return null
      }
    },

    filteredOptions() {
      const regex = new RegExp(this.filter, 'i')
      return this.options.filter(option => regex.test(option.label))
    },
  },

  mounted() {
    if (this.autoDefault) {
      this.defaultOption = this.find(this.selected) || {}
    }
  },

  methods: {
    toggleMenu() {
      this.active = !this.active
      if (this.active) {
        this.filter = ''
        this.focusSearch()
      }
    },

    selectOption(option) {
      this.active = false
      this.$emit('select', option)
    },

    find(search) {
      return this.options.find(({ handle }) => handle === search)
    },

    focusSearch() {
      if (!this.showSearch) return null

      this.$nextTick(() => {
        this.$refs.search.focus()
      })
    },
  },
}
</script>
